import React, { useState, useEffect } from 'react';
import { customersAPI } from '../services/api';

interface Customer {
  id: number;
  name: string;
  milk_run_capable: boolean;
  milk_run_region: string;
  milk_run_offload_equipment: string;
  milk_run_truck_access: boolean;
  milk_run_truck_access_notes: string;
  milk_run_notes: string;
  latitude: number | null;
  longitude: number | null;
}

interface DeliveryInfo {
  preferred_shipping_method: string | null;
  fallback_shipping_method: string | null;
  has_loading_dock: boolean;
  requires_liftgate: boolean;
  is_residential_or_farm: boolean;
  can_receive_53ft_trailer: boolean;
  preferred_terminal: string;
  min_shipment_lbs: number;
  delivery_contact_name: string;
  delivery_contact_phone: string;
  delivery_address: string;
  delivery_city: string;
  delivery_state: string;
  delivery_zip: string;
  delivery_notes: string;
}

interface Props {
  customer: Customer;
  onUpdate: () => void;
}

const SHIPPING_METHODS = [
  { value: '', label: 'Select shipping method...' },
  { value: 'will_call', label: 'Will Call - Kettle Falls' },
  { value: 'ltl_dock', label: 'LTL Ship to Dock' },
  { value: 'ltl_business', label: 'LTL Ship to Business' },
  { value: 'ltl_terminal', label: 'LTL Will Call (Terminal Pickup)' },
  { value: 'milk_run', label: 'Milk Run' },
];

const MILK_RUN_ROUTES = [
  { value: '', label: 'Select a route...' },
  { value: 'hwy2', label: 'Hwy 2 Corridor (North: Bellingham, Everett, Wenatchee, Islands)' },
  { value: 'i90', label: 'I-90 Corridor (South: Seattle, Tacoma, Olympia, Portland)' },
  { value: 'local', label: 'Local (Spokane area / pickup)' },
];

const OFFLOAD_EQUIPMENT = [
  { value: 'none', label: 'No equipment - not eligible' },
  { value: 'forklift', label: 'Forklift' },
  { value: 'tractor', label: 'Tractor with forks/loader' },
  { value: 'pallet_jack', label: 'Pallet jack (ground unload)' },
];

const SHIPMENT_MINIMUMS = [
  { value: 0, label: 'No minimum (ship every order individually)' },
  { value: 1000, label: '1,000 lbs (Half Pallet)' },
  { value: 2000, label: '2,000 lbs (Full Pallet)' },
];

const CustomerShippingPreferences: React.FC<Props> = ({ customer, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Shipping preferences form state
  const [preferredMethod, setPreferredMethod] = useState('');
  const [fallbackMethod, setFallbackMethod] = useState('');
  const [hasLoadingDock, setHasLoadingDock] = useState(false);
  const [needsLiftgate, setNeedsLiftgate] = useState(false);
  const [isResidentialFarm, setIsResidentialFarm] = useState(false);
  const [canReceive53ft, setCanReceive53ft] = useState(false);
  const [preferredTerminal, setPreferredTerminal] = useState('');
  const [minShipmentLbs, setMinShipmentLbs] = useState(0);
  const [customMinShipment, setCustomMinShipment] = useState('');
  const [showCustomMinShipment, setShowCustomMinShipment] = useState(false);

  // Delivery contact state
  const [deliveryContactName, setDeliveryContactName] = useState('');
  const [deliveryContactPhone, setDeliveryContactPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryState, setDeliveryState] = useState('WA');
  const [deliveryZip, setDeliveryZip] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');

  // Milk run settings (from Customer model)
  const [milkRunRoute, setMilkRunRoute] = useState(customer.milk_run_region || '');
  const [offloadEquipment, setOffloadEquipment] = useState(customer.milk_run_offload_equipment || 'none');
  const [truckAccessNotes, setTruckAccessNotes] = useState(customer.milk_run_truck_access_notes || '');
  const [milkRunNotes, setMilkRunNotes] = useState(customer.milk_run_notes || '');

  // Load delivery info
  useEffect(() => {
    loadDeliveryInfo();
  }, [customer.id]);

  const loadDeliveryInfo = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.getDeliveryInfo(customer.id);
      const info = response.data;

      setPreferredMethod(info.preferred_shipping_method || '');
      setFallbackMethod(info.fallback_shipping_method || '');
      setHasLoadingDock(info.has_loading_dock || false);
      setNeedsLiftgate(info.requires_liftgate || false);
      setIsResidentialFarm(info.is_residential_or_farm || false);
      setCanReceive53ft(info.can_receive_53ft_trailer || false);
      setPreferredTerminal(info.preferred_terminal || '');
      setMinShipmentLbs(info.min_shipment_lbs || 0);
      setDeliveryContactName(info.delivery_contact_name || '');
      setDeliveryContactPhone(info.delivery_contact_phone || '');
      setDeliveryAddress(info.delivery_address || '');
      setDeliveryCity(info.delivery_city || '');
      setDeliveryState(info.delivery_state || 'WA');
      setDeliveryZip(info.delivery_zip || '');
      setDeliveryNotes(info.delivery_notes || '');

      // Check if custom shipment minimum
      const isPreset = SHIPMENT_MINIMUMS.some(opt => opt.value === info.min_shipment_lbs);
      if (!isPreset && info.min_shipment_lbs > 0) {
        setShowCustomMinShipment(true);
        setCustomMinShipment(info.min_shipment_lbs.toString());
      }
    } catch (err) {
      console.error('Error loading delivery info:', err);
      // If no delivery info exists, that's ok - just use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      // Calculate effective min shipment
      const effectiveMinShipment = showCustomMinShipment
        ? parseInt(customMinShipment) || 0
        : minShipmentLbs;

      // Update delivery info
      await customersAPI.updateDeliveryInfo(customer.id, {
        preferred_shipping_method: preferredMethod || null,
        fallback_shipping_method: fallbackMethod || null,
        has_loading_dock: hasLoadingDock,
        requires_liftgate: needsLiftgate,
        is_residential_or_farm: isResidentialFarm,
        can_receive_53ft_trailer: canReceive53ft,
        preferred_terminal: preferredTerminal,
        min_shipment_lbs: effectiveMinShipment,
        delivery_contact_name: deliveryContactName,
        delivery_contact_phone: deliveryContactPhone,
        delivery_address: deliveryAddress,
        delivery_city: deliveryCity,
        delivery_state: deliveryState,
        delivery_zip: deliveryZip,
        delivery_notes: deliveryNotes,
      });

      // Update milk run fields on customer (PATCH - partial update)
      await customersAPI.patch(customer.id, {
        milk_run_offload_equipment: offloadEquipment,
        milk_run_truck_access: canReceive53ft, // Sync with 53ft capability
        milk_run_truck_access_notes: truckAccessNotes,
        milk_run_region: preferredMethod === 'milk_run' ? milkRunRoute : '',
        milk_run_notes: milkRunNotes,
      });

      setIsEditing(false);
      onUpdate();
    } catch (err) {
      console.error('Error saving shipping preferences:', err);
      setError('Failed to save shipping preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    loadDeliveryInfo(); // Reset to loaded values
    setIsEditing(false);
    setError('');
  };

  // Auto-set liftgate when needed
  useEffect(() => {
    if (preferredMethod === 'ltl_business' && !hasLoadingDock) {
      setNeedsLiftgate(true);
    }
  }, [preferredMethod, hasLoadingDock]);

  // Check milk run eligibility
  const isMilkRunEligible = offloadEquipment !== 'none' && canReceive53ft;

  // Show warning if milk run selected but not eligible
  const showMilkRunWarning = preferredMethod === 'milk_run' && !isMilkRunEligible;

  if (loading) {
    return (
      <div className="detail-section">
        <div className="loading">Loading shipping preferences...</div>
      </div>
    );
  }

  return (
    <div className="detail-section shipping-preferences-section">
      <div className="section-header">
        <h3>Shipping Preferences</h3>
        {!isEditing && (
          <button className="btn btn-secondary btn-sm" onClick={() => setIsEditing(true)}>
            Edit
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {isEditing ? (
        <div className="shipping-form">
          {/* Primary Shipping Method */}
          <div className="form-group">
            <label>Preferred Shipping Method</label>
            <select
              value={preferredMethod}
              onChange={(e) => setPreferredMethod(e.target.value)}
              className="form-select"
            >
              {SHIPPING_METHODS.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
            <p className="form-hint">Default method for new orders</p>
          </div>

          {/* Fallback Method */}
          <div className="form-group">
            <label>Fallback Method (when preferred isn't available)</label>
            <select
              value={fallbackMethod}
              onChange={(e) => setFallbackMethod(e.target.value)}
              className="form-select"
            >
              {SHIPPING_METHODS.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
            <p className="form-hint">e.g., LTL when milk run isn't scheduled</p>
          </div>

          <hr className="section-divider" />

          {/* Delivery Site Capabilities */}
          <h4>Delivery Site Capabilities</h4>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={hasLoadingDock}
                onChange={(e) => setHasLoadingDock(e.target.checked)}
              />
              <span>Has Loading Dock</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={needsLiftgate}
                onChange={(e) => setNeedsLiftgate(e.target.checked)}
              />
              <span>Needs Liftgate</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isResidentialFarm}
                onChange={(e) => setIsResidentialFarm(e.target.checked)}
              />
              <span>Residential / Farm Delivery (+$40)</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={canReceive53ft}
                onChange={(e) => setCanReceive53ft(e.target.checked)}
              />
              <span>Can Receive 53' Trailer (required for milk run)</span>
            </label>
          </div>

          {/* Milk Run Warning */}
          {showMilkRunWarning && (
            <div className="alert alert-warning">
              <strong>Milk run requires:</strong> 53' trailer access AND offload equipment.
              Please check the requirements above or select a different shipping method.
            </div>
          )}

          {/* Milk Run Details - only show if milk run selected */}
          {preferredMethod === 'milk_run' && (
            <>
              <hr className="section-divider" />
              <h4>Milk Run Details</h4>

              <div className="form-group">
                <label>Offload Equipment *</label>
                <select
                  value={offloadEquipment}
                  onChange={(e) => setOffloadEquipment(e.target.value)}
                  className="form-select"
                >
                  {OFFLOAD_EQUIPMENT.map((eq) => (
                    <option key={eq.value} value={eq.value}>
                      {eq.label}
                    </option>
                  ))}
                </select>
                <p className="form-hint">
                  Driver can't wait - customer must unload pallets quickly
                </p>
              </div>

              <div className="form-group">
                <label>Route Corridor</label>
                <select
                  value={milkRunRoute}
                  onChange={(e) => setMilkRunRoute(e.target.value)}
                  className="form-select"
                >
                  {MILK_RUN_ROUTES.map((route) => (
                    <option key={route.value} value={route.value}>
                      {route.label}
                    </option>
                  ))}
                </select>
              </div>

              {canReceive53ft && (
                <div className="form-group">
                  <label>Truck Access Notes</label>
                  <textarea
                    value={truckAccessNotes}
                    onChange={(e) => setTruckAccessNotes(e.target.value)}
                    className="form-textarea"
                    rows={2}
                    placeholder="Driveway width, turnaround area, low branches, gate codes..."
                  />
                </div>
              )}

              {/* Eligibility Status */}
              <div className={`eligibility-status ${isMilkRunEligible ? 'eligible' : 'not-eligible'}`}>
                {isMilkRunEligible ? (
                  <span className="status-badge success">Eligible for milk runs</span>
                ) : (
                  <span className="status-badge warning">
                    Not eligible - needs {offloadEquipment === 'none' ? 'equipment' : ''}
                    {offloadEquipment === 'none' && !canReceive53ft ? ' and ' : ''}
                    {!canReceive53ft ? '53ft truck access' : ''}
                  </span>
                )}
              </div>
            </>
          )}

          {/* Terminal Pickup - only show if terminal selected */}
          {preferredMethod === 'ltl_terminal' && (
            <>
              <hr className="section-divider" />
              <h4>Terminal Pickup</h4>

              <div className="form-group">
                <label>Preferred Terminal</label>
                <input
                  type="text"
                  value={preferredTerminal}
                  onChange={(e) => setPreferredTerminal(e.target.value)}
                  className="form-input"
                  placeholder="e.g., Spokane, Seattle"
                />
              </div>
            </>
          )}

          <hr className="section-divider" />

          {/* Shipment Consolidation */}
          <h4>Shipment Consolidation</h4>

          <div className="form-group">
            <label>Minimum Shipment Target</label>
            <div className="radio-group">
              {SHIPMENT_MINIMUMS.map((opt) => (
                <label key={opt.value} className="radio-label">
                  <input
                    type="radio"
                    name="minShipment"
                    checked={!showCustomMinShipment && minShipmentLbs === opt.value}
                    onChange={() => {
                      setShowCustomMinShipment(false);
                      setMinShipmentLbs(opt.value);
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
              <label className="radio-label">
                <input
                  type="radio"
                  name="minShipment"
                  checked={showCustomMinShipment}
                  onChange={() => setShowCustomMinShipment(true)}
                />
                <span>Custom: </span>
                {showCustomMinShipment && (
                  <input
                    type="number"
                    value={customMinShipment}
                    onChange={(e) => setCustomMinShipment(e.target.value)}
                    className="form-input inline-input"
                    placeholder="lbs"
                    min={0}
                    max={50000}
                  />
                )}
              </label>
            </div>
          </div>

          <hr className="section-divider" />

          {/* Delivery Contact & Instructions */}
          <h4>Delivery Contact & Instructions</h4>

          <div className="form-row">
            <div className="form-group half">
              <label>Contact Name</label>
              <input
                type="text"
                value={deliveryContactName}
                onChange={(e) => setDeliveryContactName(e.target.value)}
                className="form-input"
                placeholder="Delivery contact"
              />
            </div>
            <div className="form-group half">
              <label>Contact Phone</label>
              <input
                type="text"
                value={deliveryContactPhone}
                onChange={(e) => setDeliveryContactPhone(e.target.value)}
                className="form-input"
                placeholder="(xxx) xxx-xxxx"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Delivery Address (if different from billing)</label>
            <textarea
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              className="form-textarea"
              rows={2}
              placeholder="Street address"
            />
          </div>

          <div className="form-row">
            <div className="form-group third">
              <label>City</label>
              <input
                type="text"
                value={deliveryCity}
                onChange={(e) => setDeliveryCity(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group sixth">
              <label>State</label>
              <input
                type="text"
                value={deliveryState}
                onChange={(e) => setDeliveryState(e.target.value)}
                className="form-input"
                maxLength={2}
              />
            </div>
            <div className="form-group fourth">
              <label>ZIP</label>
              <input
                type="text"
                value={deliveryZip}
                onChange={(e) => setDeliveryZip(e.target.value)}
                className="form-input"
                maxLength={10}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Special Instructions</label>
            <textarea
              value={deliveryNotes}
              onChange={(e) => setDeliveryNotes(e.target.value)}
              className="form-textarea"
              rows={3}
              placeholder="Gate codes, delivery times, special handling..."
            />
          </div>

          <div className="form-actions">
            <button
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      ) : (
        <div className="shipping-display">
          <div className="detail-grid">
            <div className="detail-item">
              <label>Preferred Method:</label>
              <span>
                {SHIPPING_METHODS.find(m => m.value === preferredMethod)?.label || 'Not set'}
              </span>
            </div>

            <div className="detail-item">
              <label>Fallback Method:</label>
              <span>
                {SHIPPING_METHODS.find(m => m.value === fallbackMethod)?.label || 'Not set'}
              </span>
            </div>

            <div className="detail-item">
              <label>Has Loading Dock:</label>
              <span className={hasLoadingDock ? 'status-active' : 'status-inactive'}>
                {hasLoadingDock ? 'Yes' : 'No'}
              </span>
            </div>

            <div className="detail-item">
              <label>Needs Liftgate:</label>
              <span className={needsLiftgate ? 'status-active' : 'status-inactive'}>
                {needsLiftgate ? 'Yes' : 'No'}
              </span>
            </div>

            <div className="detail-item">
              <label>Residential/Farm:</label>
              <span className={isResidentialFarm ? 'status-active' : 'status-inactive'}>
                {isResidentialFarm ? 'Yes' : 'No'}
              </span>
            </div>

            <div className="detail-item">
              <label>53ft Trailer Access:</label>
              <span className={canReceive53ft ? 'status-active' : 'status-inactive'}>
                {canReceive53ft ? 'Yes' : 'No'}
              </span>
            </div>

            <div className="detail-item">
              <label>Milk Run Eligible:</label>
              <span className={customer.milk_run_capable ? 'status-active' : 'status-inactive'}>
                {customer.milk_run_capable ? 'Yes' : 'No'}
              </span>
            </div>

            {preferredMethod === 'milk_run' && (
              <div className="detail-item">
                <label>Route:</label>
                <span>
                  {MILK_RUN_ROUTES.find(r => r.value === milkRunRoute)?.label || 'Not assigned'}
                </span>
              </div>
            )}

            <div className="detail-item">
              <label>Min Shipment:</label>
              <span>
                {showCustomMinShipment
                  ? `${parseInt(customMinShipment || '0').toLocaleString()} lbs`
                  : minShipmentLbs === 0
                  ? 'No minimum'
                  : `${minShipmentLbs.toLocaleString()} lbs`}
              </span>
            </div>
          </div>

          {deliveryContactName && (
            <div className="detail-item full-width">
              <label>Delivery Contact:</label>
              <span>{deliveryContactName} {deliveryContactPhone ? `(${deliveryContactPhone})` : ''}</span>
            </div>
          )}

          {deliveryAddress && (
            <div className="detail-item full-width">
              <label>Delivery Address:</label>
              <div className="notes-block">
                {deliveryAddress}
                {deliveryCity && <><br />{deliveryCity}, {deliveryState} {deliveryZip}</>}
              </div>
            </div>
          )}

          {deliveryNotes && (
            <div className="detail-item full-width">
              <label>Special Instructions:</label>
              <div className="notes-block">{deliveryNotes}</div>
            </div>
          )}

          {customer.latitude && customer.longitude && (
            <div className="coordinates-info">
              <small>
                Geocoded: {customer.latitude.toFixed(6)}, {customer.longitude.toFixed(6)}
              </small>
            </div>
          )}
        </div>
      )}

      <style>{`
        .shipping-preferences-section .section-divider {
          border: none;
          border-top: 1px solid #e0e0e0;
          margin: 1.5rem 0;
        }

        .shipping-preferences-section h4 {
          margin: 0 0 1rem 0;
          color: #333;
          font-size: 1rem;
        }

        .shipping-preferences-section .checkbox-group {
          margin-bottom: 0.75rem;
        }

        .shipping-preferences-section .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }

        .shipping-preferences-section .checkbox-label input {
          width: 18px;
          height: 18px;
          accent-color: #3b82f6;
        }

        .shipping-preferences-section .radio-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .shipping-preferences-section .radio-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }

        .shipping-preferences-section .radio-label input[type="radio"] {
          width: 18px;
          height: 18px;
        }

        .shipping-preferences-section .inline-input {
          width: 100px;
          margin-left: 0.5rem;
        }

        .shipping-preferences-section .form-row {
          display: flex;
          gap: 1rem;
        }

        .shipping-preferences-section .form-group.half {
          flex: 1;
        }

        .shipping-preferences-section .form-group.third {
          flex: 2;
        }

        .shipping-preferences-section .form-group.sixth {
          flex: 0.5;
        }

        .shipping-preferences-section .form-group.fourth {
          flex: 1;
        }

        .shipping-preferences-section .eligibility-status {
          margin: 1rem 0;
          padding: 0.75rem;
          border-radius: 6px;
        }

        .shipping-preferences-section .eligibility-status.eligible {
          background: #e8f5e9;
        }

        .shipping-preferences-section .eligibility-status.not-eligible {
          background: #fff3e0;
        }

        .shipping-preferences-section .status-badge.success {
          color: #2e7d32;
        }

        .shipping-preferences-section .status-badge.warning {
          color: #ef6c00;
        }

        .shipping-preferences-section .alert-warning {
          background: #fff3e0;
          border: 1px solid #ffcc80;
          color: #e65100;
          padding: 0.75rem;
          border-radius: 6px;
          margin: 1rem 0;
        }

        @media (max-width: 600px) {
          .shipping-preferences-section .form-row {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default CustomerShippingPreferences;
